// packages/storefront/src/components/general/WidgetErrorBoundary.tsx
// `React` is in scope on purpose: `tsx --test` (translation/mountWidgetRoot.test.ts) compiles this file with the
// root tsconfig's `jsx: preserve`, which it runs as the classic runtime (`React.createElement`). The embed's own
// build uses the automatic runtime.
import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportWidgetError, type WidgetName } from '@tourtask/storefront/translation/reportWidgetError';

interface WidgetErrorBoundaryProps {
	widget: WidgetName;
	/**
	 * What replaces a crashed widget. `'notice'`: a one-line English notice —
	 * for widgets that own a region of the partner's page (storefront, reviews, terms).
	 * `'none'`: nothing — for a floating widget (the chat launcher), which must
	 * never paint an error box over the partner's page.
	 */
	fallback: 'notice' | 'none';
	children: ReactNode;
}

interface WidgetErrorBoundaryState {
	hasError: boolean;
}

/** The notice names the widget the visitor was looking at. */
const WIDGET_NOUN: Record<WidgetName, string> = {
	storefront: 'booking widget',
	chat: 'chat widget',
	reviews: 'reviews widget',
	// "Sorry — the terms could not be loaded."
	terms: 'terms',
};

/**
 * Every embed widget's last line of defence, wrapped around its whole tree by
 * `translation/mountWidgetRoot.tsx` — the only place roots are created.
 *
 * embed is not a page of ours — it mounts into a PARTNER's website, next to
 * their content. An uncaught render error unmounts the React tree and leaves a
 * blank rectangle in the middle of somebody's homepage, with nothing on screen
 * to say what happened or what to do. React has no default for that: without a
 * boundary, the whole tree goes.
 *
 * It also makes the repo's fail-loud data helpers safe to use here. Several of
 * them THROW on an invariant violation rather than return a plausible
 * stand-in — {@link resolveDefaultVariant} is the current example: a tour whose
 * variants carry no `isDefault` is a corrupt row, and it says so instead of
 * silently pricing the wrong variant. Its own docstring puts the obligation on
 * the caller: "Callers on inbound/write paths translate the throw at their
 * boundary (page error boundary / `Result.failure`) rather than white-screening
 * on one corrupt row." The backoffice has Next's error boundary; this is
 * embed's.
 *
 * No automatic remount: a remount would lose the visitor's checkout state and
 * hide the cause. The storefront's route-level `StorefrontRouteError` keeps its
 * Reload button; this boundary is the backstop above the router.
 *
 * Deliberately NOT a translated string: the boundary must render even when the
 * failure happened before or inside the localization provider, and a
 * `getStr` call from here could throw a second time. The copy is minimal and
 * says only what a visitor can act on.
 */
class WidgetErrorBoundary extends Component<WidgetErrorBoundaryProps, WidgetErrorBoundaryState> {
	state: WidgetErrorBoundaryState = { hasError: false };

	static getDerivedStateFromError(): WidgetErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		// The partner's page owns the console, and the telemetry sink is the only
		// trace an operator or a support session has to work from. The root's
		// `onCaughtError` reports the same error object first; the reporter drops
		// the duplicate, so this only matters for a boundary outside such a root.
		reportWidgetError({
			widget: this.props.widget,
			kind: 'caught',
			error,
			componentStack: info.componentStack ?? undefined,
		});
	}

	render(): ReactNode {
		if (!this.state.hasError) return this.props.children;
		if (this.props.fallback === 'none') return null;
		return (
			<div
				style={{
					padding: '24px',
					textAlign: 'center',
					fontFamily: 'system-ui, -apple-system, sans-serif',
					color: '#b3261e',
				}}
			>
				{/* One string, one text node: a page translator gets the sentence whole. */}
				{`Sorry — the ${WIDGET_NOUN[this.props.widget]} could not be loaded. Please reload the page.`}
			</div>
		);
	}
}

export default WidgetErrorBoundary;
